import React from 'react'
import {
  isReactNativeApp,
  isReactNativeIos,
  AppVersion,
  sendToNative,
} from '@mobi/web-native-comms/web'
import { Button } from '@mobi/component-library/Common/Buttons'
import { Icon } from '@mobi/component-library/Common/Icon'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { store } from '@core/Store'
import { closeModal, openModal } from '@core/Components/Modal'
import { trackEvent } from '@classic/Foundation/Analytics/GoogleTagManagerService'
import type { BlackbookEntry } from '@core/Areas/Blackbook/types'
import { generateShortenedUrl } from '@core/Areas/ShortenedUrls/api'
import { SHARE_BLACKBOOK_SEARCH_PARAM } from '@core/Areas/Blackbook/constants'
import { ShareLoading } from './Components/ShareLoading'

const enum LocalConstants {
  ModalId = 'blackbook-share-loading',
}

export const ShareButton: React.FC<ShareButtonProps> = ({ name, type }) => {
  if (!isReactNativeApp || !AppVersion || +AppVersion.split('.')[0] < 208 || !(name && type))
    return null

  const handleClick = () => {
    trackBlackbookShare({ name, type })
    handleBlackbookShareClick({ name, type })
  }

  return (
    <Button onClick={handleClick} color='link' data-testid='blackbook-runner-share-button'>
      <Icon type={isReactNativeIos ? 'shareIos' : 'shareAndroid'} size='1.3rem' /> Share
    </Button>
  )
}

// =============
// Local Helpers
// =============

const handleBlackbookShareClick = async ({ name, type }: ShareButtonProps) => {
  try {
    store.dispatch(
      openModal({
        id: LocalConstants.ModalId,
        modalComponent: ShareLoading,
        isDismissable: false,
        position: 'middle',
        cornerStyle: 'round',
      })
    )

    const shareLink = await buildLinkForShare({ name, type })

    sendToNative('SHARE_SHEET_REQUEST', {
      message: `${name} is one of my favourites. You should add it to your TABtouch Blackbook here:`,
      url: shareLink.toString(),
    })
  } catch {
    addToast({
      icon: 'error',
      timeoutMs: Infinity,
      id: 'blackbook-share-cancel',
      title: 'Unable to Create Share',
    })
  } finally {
    store.dispatch(closeModal({ id: LocalConstants.ModalId }))
  }
}

const buildLinkForShare = async ({ name, type }: ShareButtonProps): Promise<URL> => {
  let rawLink = `${window.location.origin}/?${SHARE_BLACKBOOK_SEARCH_PARAM}=${type},${name}`
  if (isReactNativeIos) {
    rawLink = rawLink.replace('customscheme://', 'https://')
  }

  return await generateShortenedUrl(new URL(rawLink))
}

const trackBlackbookShare = (data: ShareButtonProps) => {
  trackEvent('share-blackbook', data)
}

// =====
// Types
// =====

type ShareButtonProps = { name: string; type: BlackbookEntry['Code'] }
